import { Link } from 'react-router-dom';
import { aboutInfo, testimonials } from '@/mocks/about';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

export default function About() {
  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于征途国际',
      description: '征途国际品牌介绍、发展历程、用户口碑和联系方式',
      url: 'https://ztroute.github.io/about',
      mainEntity: {
        '@type': 'Organization',
        name: '征途国际',
        description: aboutInfo.brandIntro,
        url: 'https://ztroute.github.io',
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutInfo.contact.email,
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      },
    },
  ];

  return (
    <>
      <SEOHead
        title="关于征途国际-品牌介绍与联系方式 | 征途国际官方下载站"
        description="征途国际品牌介绍、发展历程、用户口碑评价和官方联系方式。了解征途国际的热血国战MMORPG游戏世界。"
        keywords="征途国际,关于征途国际,征途国际联系方式,征途国际客服"
        canonicalUrl="https://ztroute.github.io/about"
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-20 pb-14 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Intro */}
          <section className="mb-16">
            <div className="max-w-3xl">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于征途国际
              </h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                {aboutInfo.brandIntro}
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                征途国际以「热血、兄弟、荣耀」为核心价值观，致力于为全球玩家打造最真实、最热血的国战体验。我们拥有一支超过500人的专业研发和运营团队，持续为游戏注入新内容和优化体验。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {aboutInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-xl p-6 border border-background-200/70 text-center"
                >
                  <div className="w-11 h-11 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* History Timeline */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8">
                {aboutInfo.history.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row gap-6 md:gap-10 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex items-start gap-4 md:justify-end">
                      <div className={`flex-1 bg-background-100 rounded-xl p-5 border border-background-200/70 ${
                        index % 2 === 0 ? 'md:text-right' : ''
                      }`}>
                        <span className="inline-block text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium mb-2">
                          {item.year}
                        </span>
                        <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-primary-500 rounded-full ring-4 ring-background-50 top-6"></div>
                    <div className="md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8">
              玩家口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-100 rounded-xl p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-5">
                  官方联系方式
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服邮箱</div>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">微信公众号</div>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">官方微博</div>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-qq-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">QQ客服群</div>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.qq}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-5">
                  公司信息
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">公司地址</div>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.workTime}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-background-200">
                  <p className="text-xs text-foreground-400 leading-relaxed">
                    如需商务合作或媒体采访，请发送邮件至我们的官方邮箱。我们的团队将在1-2个工作日内回复您的咨询。
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-background-100 rounded-xl p-8 border border-background-200/70 text-center">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">
                加入征途国际，开启你的热血国战之旅
              </h3>
              <p className="text-sm text-foreground-600 mb-5">
                全球5000万玩家的共同选择，现在就下载，体验最热血的国战MMORPG
              </p>
              <Link
                to="/"
                className="inline-flex px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap items-center gap-2"
              >
                <i className="ri-download-line"></i>
                立即下载
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}